import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, User, LogOut, LayoutDashboard, Heart, Shield, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../lib/auth";

const navLinks = [
  { to: "/cars", label: "Browse Cars" },
  { to: "/sell", label: "Sell Your Car" },
  { to: "/dealer-signup", label: "Become a Dealer" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const Brand = (
    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo-link">
      <div className="relative w-10 h-10 rounded-md bg-[#0A192F] grid place-items-center">
        <span className="font-heading font-extrabold text-[#F97316] text-lg">GJ</span>
        <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-[#F97316] border-2 border-white" />
      </div>
      <div className="leading-tight">
        <div className="font-heading font-extrabold tracking-tight text-[#0A192F] text-lg">
          Gaddi Junction
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#718096]">
          Affordable · Trusted
        </div>
      </div>
    </Link>
  );

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[#0A192F]/10"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        {Brand}

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}-link`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#0A192F]" : "text-[#4A5568] hover:text-[#0A192F]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/cars")}
            className="text-[#0A192F]"
            data-testid="navbar-search-btn"
          >
            <Search className="w-4 h-4" />
          </Button>

          {user && user.id ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#0A192F]/15 text-[#0A192F]"
                  data-testid="navbar-account-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  {user.name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-[#718096] uppercase">
                  {user.role}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(user.role === "dealer" || user.role === "admin") && (
                  <DropdownMenuItem
                    onClick={() => navigate("/dealer")}
                    data-testid="menu-dealer-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dealer Dashboard
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    data-testid="menu-admin"
                  >
                    <Shield className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/favourites")} data-testid="menu-favourites">
                  <Heart className="w-4 h-4 mr-2" /> Favourites
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] text-[#718096] uppercase tracking-wider">
                  Legal
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/terms")} data-testid="menu-terms">
                  <FileText className="w-4 h-4 mr-2" /> Terms &amp; Conditions
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/privacy")} data-testid="menu-privacy">
                  <FileText className="w-4 h-4 mr-2" /> Privacy Policy
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/disclaimer")} data-testid="menu-disclaimer">
                  <FileText className="w-4 h-4 mr-2" /> Disclaimer
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                className="text-[#0A192F]"
                data-testid="navbar-login-btn"
              >
                Login
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/register")}
                className="bg-[#0A192F] hover:bg-[#112240] text-white"
                data-testid="navbar-signup-btn"
              >
                Sign up
              </Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="navbar-mobile-toggle">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white">
              <div className="flex flex-col gap-2 mt-8">
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="px-3 py-3 rounded-md hover:bg-[#F3F0EA] text-[#0A192F] font-medium"
                    data-testid={`mobile-nav-${l.to.slice(1)}-link`}
                  >
                    {l.label}
                  </Link>
                ))}
                <div className="h-px bg-[#0A192F]/10 my-2" />
                {user && user.id ? (
                  <>
                    {(user.role === "dealer" || user.role === "admin") && (
                      <Link to="/dealer" className="px-3 py-3 rounded-md hover:bg-[#F3F0EA] text-[#0A192F]">
                        Dealer Dashboard
                      </Link>
                    )}
                    {user.role === "admin" && (
                      <Link to="/admin" className="px-3 py-3 rounded-md hover:bg-[#F3F0EA] text-[#0A192F]">
                        Admin Panel
                      </Link>
                    )}
                    <Link to="/profile" className="px-3 py-3 rounded-md hover:bg-[#F3F0EA] text-[#0A192F]" data-testid="mobile-nav-profile-link">
                      My Profile
                    </Link>
                    <button
                      onClick={logout}
                      className="text-left px-3 py-3 rounded-md hover:bg-[#F3F0EA] text-[#9B2C2C]"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="px-3 py-3 rounded-md hover:bg-[#F3F0EA] text-[#0A192F]">
                      Login
                    </Link>
                    <Link to="/register" className="px-3 py-3 rounded-md bg-[#0A192F] text-white text-center">
                      Sign up
                    </Link>
                  </>
                )}
                <div className="h-px bg-[#0A192F]/10 my-2" />
                <div className="px-3 text-[10px] uppercase tracking-wider text-[#718096] font-semibold">
                  Legal
                </div>
                <Link to="/terms" className="px-3 py-2.5 rounded-md hover:bg-[#F3F0EA] text-[#4A5568] text-sm" data-testid="mobile-nav-terms-link">
                  Terms &amp; Conditions
                </Link>
                <Link to="/privacy" className="px-3 py-2.5 rounded-md hover:bg-[#F3F0EA] text-[#4A5568] text-sm" data-testid="mobile-nav-privacy-link">
                  Privacy Policy
                </Link>
                <Link to="/disclaimer" className="px-3 py-2.5 rounded-md hover:bg-[#F3F0EA] text-[#4A5568] text-sm" data-testid="mobile-nav-disclaimer-link">
                  Disclaimer
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
